import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  getSafeTimezone,
  getCurrentHourInTimezone,
  isTimeStaleForToday,
  resolveFixedChargeDayStartDate,
  snapScheduledEndDate,
} from '@charitystack/shared';

// Re-exported from @charitystack/shared so existing importers of these from
// scheduleConfigUtils keep working. The implementations now live in
// shared/src/utils/fixedChargeDayUtils.js (single source of truth, also used by
// the dashboard fundraiser-config validator). See PE-1569.
export {
  getCurrentHourInTimezone,
  isTimeStaleForToday,
  resolveFixedChargeDayStartDate,
};

// Extend dayjs with UTC and timezone plugins for proper timezone handling
dayjs.extend(utc);
dayjs.extend(timezone);

// Map backend frequency format to dashboard format for schedule config lookup
const BACKEND_TO_DASHBOARD_FREQ_MAP = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  ANNUALLY: 'Annually',
};

/**
 * Get schedule config for a specific frequency
 * Handles both old (single config) and new (per-frequency) formats
 * @param {Object} defaultScheduledRange - The schedule range config from backend
 * @param {string} frequency - The backend frequency format (e.g., 'MONTHLY')
 * @returns {Object|null} The schedule config or null if not found
 */
export const getScheduleConfigForFrequency = (defaultScheduledRange, frequency) => {
  if (!defaultScheduledRange || frequency === 'ONE_TIME') return null;

  const dashboardFreq = BACKEND_TO_DASHBOARD_FREQ_MAP[frequency];

  // New format: per-frequency config (has frequency keys like Daily, Weekly, Monthly, Annually)
  if (dashboardFreq && defaultScheduledRange[dashboardFreq]) {
    return defaultScheduledRange[dashboardFreq];
  }

  // Legacy format: single global config (has 'enabled' or 'scheduleType' at top level)
  if (
    defaultScheduledRange.enabled !== undefined ||
    defaultScheduledRange.scheduleType !== undefined
  ) {
    return defaultScheduledRange;
  }

  return null;
};

/**
 * Check if a date has passed (is before today)
 * @param {string} dateString - ISO date string (YYYY-MM-DD)
 * @returns {boolean} True if the date has passed
 */
export const isDatePassed = (dateString) => {
  if (!dateString) return false;
  const date = dayjs(dateString);
  const today = dayjs().startOf('day');
  return date.isBefore(today);
};

// resolveFixedChargeDayStartDate now lives in @charitystack/shared
// (fixedChargeDayUtils) and is imported + re-exported at the top of this file.

/**
 * Calculate scheduled dates based on config
 * Supports dynamic (offset-based) dates, fixed dates, and a fixed recurring charge day.
 * @param {Object} scheduleConfig - The schedule configuration object
 * @param {string} [frequency] - Backend frequency ('WEEKLY'|'MONTHLY'|'ANNUALLY'|'DAILY').
 *   Required to interpret `fixedChargeDay`; omitting it skips fixed-charge-day resolution
 *   (backward compatible).
 * @returns {Object} Object containing { startDate, endDate, timeValue, shouldAutoExpand, isLockedSchedule }
 */
export const calculateScheduledDates = (scheduleConfig, frequency = null) => {
  if (!scheduleConfig) {
    return {
      startDate: null,
      endDate: null,
      timeValue: null,
      shouldAutoExpand: false,
      isLockedSchedule: false,
    };
  }

  const {
    scheduleType,
    startDateOffset,
    endDateOffset,
    scheduledTime,
    // New fields for fixed dates
    startDateType = 'dynamic', // default to dynamic for backward compatibility
    endDateType = 'dynamic',
    fixedStartDate,
    fixedEndDate,
    // Per-frequency lock setting
    lockedScheduleDate = false,
    // Org-fixed recurring charge day (anchor). Frequency-interpreted; see
    // resolveFixedChargeDayStartDate. Absent => legacy start/end behavior.
    fixedChargeDay = null,
  } = scheduleConfig;

  // Map schedule type to dayjs unit (for dynamic dates)
  const unitMap = {
    days: 'day',
    weeks: 'week',
    months: 'month',
    years: 'year',
  };
  const unit = unitMap[scheduleType] || 'day';

  // The configured window start (fixed date or resolved dynamic offset), if any.
  // Used to anchor the fixed charge day on/after the window opens — so a future
  // "starts June 11" window yields the first matching day on/after June 11
  // instead of the next one from today.
  let windowStart = null;
  if (startDateType === 'fixed' && fixedStartDate && !isDatePassed(fixedStartDate)) {
    windowStart = fixedStartDate;
  } else if (startDateType === 'dynamic' && startDateOffset && startDateOffset > 0) {
    windowStart = dayjs().add(startDateOffset, unit).format('YYYY-MM-DD');
  }

  // Resolve the fixed-charge-day anchor up front. When set, it drives the start
  // date (the recurring day inside any start/end window) and must take precedence
  // over the "immediate" early-return below.
  const hasFixedChargeDay = fixedChargeDay !== null && fixedChargeDay !== undefined && fixedChargeDay !== '';
  const fixedChargeDayStartDate = hasFixedChargeDay
    ? resolveFixedChargeDayStartDate(fixedChargeDay, frequency, scheduledTime, windowStart)
    : null;

  // If both are dynamic and scheduleType is immediate, return defaults.
  // Skipped when a fixed charge day is configured — the anchor must still resolve.
  if (!hasFixedChargeDay && startDateType === 'dynamic' && endDateType === 'dynamic') {
    if (scheduleType === 'immediate' || !scheduleType) {
      return {
        startDate: null,
        endDate: null,
        timeValue: 'now',
        shouldAutoExpand: false,
      };
    }
  }

  // Calculate start date.
  // A fixed charge day takes precedence: it defines the recurring day the donor is
  // charged on. The start/end window (below) only bounds when that recurrence is active.
  let startDate = null;
  if (fixedChargeDayStartDate) {
    startDate = fixedChargeDayStartDate;
  } else if (startDateType === 'fixed' && fixedStartDate) {
    // Fixed date - check if it has passed
    if (!isDatePassed(fixedStartDate)) {
      startDate = fixedStartDate;
    }
    // If fixed date has passed, startDate remains null
  } else if (startDateType === 'dynamic' && startDateOffset && startDateOffset > 0) {
    // Dynamic date - calculate from today
    startDate = dayjs().add(startDateOffset, unit).format('YYYY-MM-DD');
  }

  // Calculate end date
  let endDate = null;
  if (endDateType === 'fixed' && fixedEndDate) {
    // Fixed date - check if it has passed
    if (!isDatePassed(fixedEndDate)) {
      endDate = fixedEndDate;
    }
    // If fixed date has passed, endDate remains null
  } else if (endDateType === 'dynamic' && endDateOffset && endDateOffset > 0) {
    // Dynamic date - calculate from today
    endDate = dayjs().add(endDateOffset, unit).format('YYYY-MM-DD');
  }

  // Process time value
  let timeValue = 'now';
  if (scheduledTime && scheduledTime.trim() !== '') {
    // If it's already in HH:mm format, use it directly
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(scheduledTime)) {
      timeValue = scheduledTime.substring(0, 5);
    } else {
      // Try to parse as date-time and extract time
      const parsedTime = dayjs(scheduledTime);
      if (parsedTime.isValid()) {
        timeValue = parsedTime.format('HH:mm');
      }
    }
  }

  // Determine if accordion should auto-expand (when dates are configured or schedule is locked)
  const shouldAutoExpand = startDate !== null || endDate !== null || lockedScheduleDate;

  // Only mark as locked schedule if locked AND this frequency has configured dates.
  // This prevents showing empty locked schedule UI for frequencies without configuration.
  // A fixed charge day counts as a configured schedule even when it resolves to a start
  // date — and explicitly so an anchor-only locked config still locks.
  const hasConfiguredDates =
    startDate !== null || endDate !== null || hasFixedChargeDay || (timeValue && timeValue !== 'now');
  const isLockedSchedule = lockedScheduleDate && hasConfiguredDates;

  return {
    startDate,
    endDate,
    timeValue,
    shouldAutoExpand,
    isLockedSchedule,
  };
};

/**
 * Check whether a schedule selection has any meaningful configured value.
 * "now" does not count as scheduled time by itself.
 *
 * @param {Object} selection
 * @param {string|Date|dayjs.Dayjs|null} [selection.startDate]
 * @param {string|Date|dayjs.Dayjs|null} [selection.endDate]
 * @param {string|null} [selection.timeValue]
 * @returns {boolean}
 */
/** The picker's recurrence period for a donation frequency (ControlledRecurringDatePicker `timePicker`). */
export const frequencyToTimePicker = (frequency) => {
  switch (frequency) {
    case 'WEEKLY': return 'week';
    case 'MONTHLY': return 'month';
    case 'ANNUALLY': return 'year';
    default: return 'day';
  }
};

/**
 * Re-fit a saved schedule's end date to a new repeat frequency (owner call 2026-10-21: changing
 * the frequency keeps the schedule). Applies the picker's own clamp (snapScheduledEndDate) for
 * the new frequency. Returns the new end date, or null when the end date already fits (or there
 * is none).
 */
export const refitScheduledEndDate = ({ startDate, endDate, frequency }) => snapScheduledEndDate({
  startValue: startDate || null,
  endValue: endDate || null,
  timePicker: frequencyToTimePicker(frequency),
  today: dayjs().startOf('day'),
});

export const hasScheduledSelection = ({ startDate, endDate, timeValue }) =>
  Boolean(startDate || endDate || (timeValue && timeValue !== 'now'));

/**
 * Create a config key for comparing schedule configurations
 * Used to detect if schedule configuration has changed
 * @param {Object} scheduleConfig - The schedule configuration object
 * @param {string} frequency - The frequency (e.g., 'DAILY', 'MONTHLY') - important for uniqueness
 * @returns {string} JSON string key for comparison
 */
export const createScheduleConfigKey = (scheduleConfig, frequency = '') => {
  if (!scheduleConfig) return `{"frequency":"${frequency}","lockedScheduleDate":false}`;

  return JSON.stringify({
    // Include frequency to ensure switching frequencies always re-applies config
    frequency,
    scheduleType: scheduleConfig.scheduleType,
    startDateOffset: scheduleConfig.startDateOffset,
    endDateOffset: scheduleConfig.endDateOffset,
    scheduledTime: scheduleConfig.scheduledTime,
    enabled: scheduleConfig.enabled,
    // New fields for fixed dates
    startDateType: scheduleConfig.startDateType,
    endDateType: scheduleConfig.endDateType,
    fixedStartDate: scheduleConfig.fixedStartDate,
    fixedEndDate: scheduleConfig.fixedEndDate,
    // Per-frequency locked schedule setting
    lockedScheduleDate: scheduleConfig?.lockedScheduleDate,
    // Org-fixed recurring charge day — must invalidate the memo when the anchor changes
    fixedChargeDay: scheduleConfig?.fixedChargeDay,
  });
};

/**
 * Convert local date and time to UTC ISO string for postMessage payload
 *
 * This function normalizes user-selected dates/times (in browser local timezone)
 * to UTC before sending to checkout. This ensures consistent scheduling behavior
 * regardless of the user's geographic location.
 *
 * @param {string|dayjs.Dayjs|null} dateValue - Date in YYYY-MM-DD format or dayjs object
 * @param {string|null} timeValue - Time in HH:mm format (e.g., "14:00") or "now"
 * @returns {Object} Object containing:
 *   - utcDateTime: Full UTC ISO string (e.g., "2026-01-29T19:00:00Z") or null
 *   - userTimezone: The user's IANA timezone identifier (e.g., "America/New_York")
 *   - originalDate: The original date string for backward compatibility
 *   - originalTime: The original time string for backward compatibility
 *
 * @example
 * // User in EST selects Jan 29, 2026 at 2:00 PM
 * convertToUTC("2026-01-29", "14:00")
 * // Returns: {
 * //   utcDateTime: "2026-01-29T19:00:00Z",  // 2 PM EST = 7 PM UTC
 * //   userTimezone: "America/New_York",
 * //   originalDate: "2026-01-29",
 * //   originalTime: "14:00"
 * // }
 *
 * @example
 * // timeValue is "now" - return null utcDateTime (backend handles immediate start)
 * convertToUTC("2026-01-29", "now")
 * // Returns: {
 * //   utcDateTime: null,
 * //   userTimezone: "America/New_York",
 * //   originalDate: "2026-01-29",
 * //   originalTime: "now"
 * // }
 */
export const convertToUTC = (dateValue, timeValue) => {
  // Get user's timezone using the browser's Intl API
  const userTimezone = getSafeTimezone();

  // Normalize date to string format
  let dateString = null;
  if (dateValue) {
    if (typeof dateValue === 'object' && dateValue.format) {
      // dayjs object
      dateString = dateValue.format('YYYY-MM-DD');
    } else if (dateValue instanceof Date) {
      // Native Date object
      dateString = dayjs(dateValue).format('YYYY-MM-DD');
    } else if (typeof dateValue === 'string') {
      dateString = dateValue;
    }
  }

  // Base result with original values for backward compatibility
  const result = {
    utcDateTime: null,
    userTimezone,
    originalDate: dateString,
    originalTime: timeValue || null,
  };

  // If no date, return early
  if (!dateString) {
    return result;
  }

  // If timeValue is "now" or null/undefined, don't compute UTC datetime
  // Backend handles "now" specially for immediate processing
  if (!timeValue || timeValue === 'now') {
    return result;
  }

  // Parse time value (expected format: "HH:mm")
  const timeMatch = timeValue.match(/^(\d{2}):(\d{2})$/);
  if (!timeMatch) {
    // Invalid time format, return without UTC conversion
    console.warn('[convertToUTC] Invalid time format:', timeValue);
    return result;
  }

  const [, hours, minutes] = timeMatch;

  // Create a datetime string in the user's local timezone
  // Format: "YYYY-MM-DD HH:mm" parsed in user's timezone
  const localDateTimeString = `${dateString} ${hours}:${minutes}`;

  // Parse as local time in user's timezone, then convert to UTC
  const localDateTime = dayjs.tz(localDateTimeString, userTimezone);

  if (!localDateTime.isValid()) {
    console.warn('[convertToUTC] Invalid datetime:', localDateTimeString);
    return result;
  }

  // Convert to UTC and format as ISO string with Z suffix
  result.utcDateTime = localDateTime.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');

  return result;
};

/**
 * Prepare scheduled donation data for postMessage payload
 * Sends BOTH local datetime (for UI display) and UTC datetime (for payment API)
 *
 * CRITICAL FIX: When time="now", we MUST still send date information with UTC conversion
 * - User selecting a START DATE means "start on this date at current time"
 * - Backend needs UTC datetime to schedule correctly across timezones
 * - "now" only means "no specific time selected" when NO dates are selected
 *
 * @param {string|dayjs.Dayjs|null} startDate - Start date (local, YYYY-MM-DD)
 * @param {string|dayjs.Dayjs|null} endDate - End date (local, YYYY-MM-DD)
 * @param {string|null} timeValue - Time value ("HH:mm" or "now")
 * @returns {Object} Payload fields for postMessage:
 *   - localStartDateTime: Local datetime for UI display (e.g., "2026-01-29T14:00:00" or "2026-01-29T00:00:00" when time="now")
 *   - localEndDateTime: Local datetime for UI display
 *   - utcStartDateTime: UTC datetime for payment API (e.g., "2026-01-29T19:00:00Z") - ALWAYS set when date exists
 *   - utcEndDateTime: UTC datetime for payment API - ALWAYS set when date exists
 *   - userTimezone: User's IANA timezone for logging (e.g., "America/New_York")
 *   - timeValue: Original time value for midnight edge case distinction
 */
export const prepareScheduledDonationPayload = (startDate, endDate, timeValue) => {
  // Get user's timezone
  const userTimezone = getSafeTimezone();
  
  // Helper to convert date/time to both local and UTC ISO strings.
  //
  // `isStartDate` controls whether the stale-today guard is allowed to
  // advance the date by one day. For start dates this is correct (donor
  // intent is "start at the next valid slot"). For end dates we must NOT
  // advance — a stale end means the subscription has expired, not that the
  // backend should add an extra charge cycle.
  const convertDateToISOStrings = (dateValue, timeVal, isStartDate = true) => {
    if (!dateValue) {
      return { local: null, utc: null };
    }

    // Normalize date to string format
    let dateString = null;
    if (typeof dateValue === 'object' && dateValue.format) {
      dateString = dateValue.format('YYYY-MM-DD');
    } else if (dateValue instanceof Date) {
      dateString = dayjs(dateValue).format('YYYY-MM-DD');
    } else if (typeof dateValue === 'string') {
      dateString = dateValue;
    }

    if (!dateString) {
      return { local: null, utc: null };
    }

    // OPTIMIZATION: If start date is TODAY and time is "now", treat as immediate donation
    // This is functionally equivalent to no scheduling (charging right now)
    if (timeVal === 'now') {
      const selectedDate = dayjs(dateString).startOf('day');
      const today = dayjs().startOf('day');

      if (selectedDate.isSame(today, 'day')) {
        // Today + "now" = immediate donation (no scheduling)
        // Return null to indicate no scheduled start time
        return { local: null, utc: null };
      }
    }

    // Determine time to use
    let hours = '00';
    let minutes = '00';

    if (timeVal === 'now') {
      // "now" on a future date means "start of the next hour"
      // e.g., if current time is 3:45 PM, use 4:00 PM (16:00)
      const nextHour = dayjs().add(1, 'hour').startOf('hour');
      hours = nextHour.format('HH');
      minutes = '00';
    } else if (timeVal) {
      const timeMatch = timeVal.match(/^(\d{2}):(\d{2})$/);
      if (timeMatch) {
        [, hours, minutes] = timeMatch;
      }
    }

    // Normalize hour "24" (invalid ISO-8601; OpenSearch's
    // strict_date_optional_time parser rejects "YYYY-MM-DDT24:00:00") to
    // next-day "00:00". Historically the time picker emitted "24:00" when
    // the user's local hour was 23.
    if (hours === '24') {
      dateString = dayjs(dateString).add(1, 'day').format('YYYY-MM-DD');
      hours = '00';
    }

    // Stale-today guard (start dates only): if the resulting datetime falls
    // on today's date in the user's timezone AND is at-or-before "now",
    // advance dateString by one day. Covers the picker's "00:00 after 23:00
    // local" auto-default (which semantically means midnight rollover but
    // literally still says today) and any other caller producing a stale
    // today+past-time combination (CSV import, URL prefill, manual API
    // consumer). Skipped for end dates: a stale end means the subscription
    // has expired — silently advancing it would extend the schedule and
    // bill an extra cycle the donor never authorized.
    const nowInTz = dayjs().tz(userTimezone);
    let localDateTime = dayjs.tz(`${dateString} ${hours}:${minutes}`, userTimezone);
    if (
      isStartDate &&
      dayjs.tz(dateString, userTimezone).isSame(nowInTz, 'day') &&
      !localDateTime.isAfter(nowInTz)
    ) {
      dateString = nowInTz.add(1, 'day').format('YYYY-MM-DD');
      localDateTime = dayjs.tz(`${dateString} ${hours}:${minutes}`, userTimezone);
    }

    const localDateTimeString = `${dateString}T${hours}:${minutes}:00`;
    const utcDateTime = localDateTime.utc().format('YYYY-MM-DDTHH:mm:ss[Z]');

    return {
      local: localDateTimeString,
      utc: utcDateTime,
    };
  };

  // If no start date but a specific time was chosen (not "now"), default to today.
  // This ensures the backend receives a startValue and schedules the charge
  // for the selected time instead of charging immediately.
  let effectiveStartDate = startDate;
  if (!startDate && timeValue && timeValue !== 'now') {
    effectiveStartDate = dayjs().format('YYYY-MM-DD');
  }

  // Convert both start and end dates. End dates pass isStartDate=false so
  // the stale-today guard doesn't silently extend an expired subscription.
  const startResult = convertDateToISOStrings(effectiveStartDate, timeValue, true);
  const endResult = convertDateToISOStrings(endDate, timeValue, false);

  const payload = {
    // Local datetime fields (for UI display in checkout)
    localStartDateTime: startResult.local,
    localEndDateTime: endResult.local,

    // UTC datetime fields (for payment API submission)
    utcStartDateTime: startResult.utc,
    utcEndDateTime: endResult.utc,

    // User's timezone for logging/audit trail
    userTimezone: userTimezone,

    // Original timeValue for midnight edge case distinction
    // Allows checkout to distinguish "now" from explicitly selected "00:00"
    timeValue: timeValue,
  };

  return payload;
};

// getCurrentHourInTimezone and isTimeStaleForToday now live in
// @charitystack/shared (fixedChargeDayUtils) and are imported + re-exported at
// the top of this file.
